import { Col, Container, Placeholder, Row, Stack } from 'react-bootstrap'
import React, { useMemo, useState } from 'react'
import { EditableText } from '../../common/EditableText/EditableText'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import {
  selectRoom,
  selectRoomSession,
  selectRoomSessionIsLoading,
  selectRoomSessionMeetingTitle,
  setRoomSession,
} from '../../../../../store/meetingSummary/roomSession'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomTrackingDetails } from '../../tracking'
import { parseMetadata } from '../../../../../common/util'
import { AssistantStatusBadge } from '../../../../../views/LiveMeetings/AssistantStatusBadge'
import { LiveBadge } from '../../../../../views/LiveMeetings/LiveBadge'
import { MeetingProgressDisplay } from './MeetingProgressDisplay'
import { useUpdateRoomSessionMetadata } from '../../MeetingSummary/hooks/api'
import { ChangeMeetingOptions } from '../../../../../views/LiveMeetings/ChangeMeetingOptions'
import { useReloadRoomSession } from '../hooks/api'
import { selectPreparationStopOnly } from '../../../../../store/meetingSummary/display'
import { PrivateBadge } from '../../../../../views/LiveMeetings/PrivateBadge'
import { AddAIAssistantButton } from '../../../../../views/LiveMeetings/AddAIAssistantButton'
import { MeetingType } from '../../MeetingSummary/MeetingInfo/MeetingType'
import { CreatorType } from '../../MeetingSummary/MeetingInfo/CreatorType'
import { MeetingDateAndTime } from '../../common/MeetingDateAndTime'
import { isLiveSession } from '../../../../../common/session.util'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useUpdateSessionConfigMutation } from '../../../../../store/media/media'
import { LanguageConfigSource, MeetingLanguage, RoomSessionLanguageConfig } from '../../../../../models/Room'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { PrivacyControl } from '../../MeetingSummary/MeetingInfo/PrivacyControl'
import { usePrivateMeetingsEnabled } from '../../../../../common/usePrivateMeetingsEnabled'
import { useMultiLanguageEnabled } from '../../../../../common/useMultiLanguageEnabled'
import { LanguageInfoButton } from '../../../../../views/LanguageSupport/LanguageInfo'
import { ChangeMeetingLanguageModal } from '../../../../../views/LanguageSupport/ChangeMeetingLanguageModal'

export const MeetingInfo: React.FC = () => {
  const { tracked } = useTracking()
  const dispatch = useAppDispatch()
  const roomSession = useAppSelector(selectRoomSession)
  const room = useAppSelector(selectRoom)
  const accountId = useAppSelector(selectAccountId)
  const roomSessionIsLoading = useAppSelector(selectRoomSessionIsLoading)
  const meetingTitle = useAppSelector(selectRoomSessionMeetingTitle)
  const preparationStopOnly = useAppSelector(selectPreparationStopOnly)
  const updateMetadata = useUpdateRoomSessionMetadata()
  const reloadRoomSession = useReloadRoomSession()
  const [updateRoomSessionConfig, { isLoading: isUpdatingSessionConfig }] = useUpdateSessionConfigMutation()
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const { hasQuota } = useZocksQuota()
  const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')
  const { privateMeetingsEnabled } = usePrivateMeetingsEnabled()
  const { multiLanguageEnabled } = useMultiLanguageEnabled()

  const {
    creator_service_id: creatorServiceId,
    source = 'voice-memo',
    zocksAssistantStatus = 'notStarted',
  } = useMemo(() => (roomSession ? parseMetadata(roomSession.metadata) : {}), [roomSession])

  const expectedDurationMs = useMemo(() => {
    if (!room || !room.config || !room.config.joinWindows || !room.config.joinWindows.length) {
      return null
    }
    const { config: { joinWindows: [{ start = null, end = null }] = [{}] } = {} } = room
    return start && end ? new Date(end).getTime() - new Date(start).getTime() : null
  }, [room])

  if (!roomSession || roomSessionIsLoading) {
    return (
      <Container className='w-100 mw-100 p-3'>
        <Row className='m-0'>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={4} bg={'light'} />
          </Placeholder>
        </Row>
        <Row className='mt-3 d-flex flex-column zocks-gap-1.25'>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={2} bg={'light'} />
          </Placeholder>
        </Row>
      </Container>
    )
  }

  const roomSessionLanguageConfig: RoomSessionLanguageConfig = roomSession.config?.language ?? {
    language: MeetingLanguage.ENGLISH,
    source: LanguageConfigSource.DEFAULT,
  }
  const calculatedRoomSessionLanguageConfig = roomSession.config?.calculatedLanguage ?? null

  const { startedAt, finishedAt } = roomSession

  const _handleMeetingTitleChanged = async (newTitle: string) => {
    if (!roomSession) {
      return
    }

    const currentMetadata = parseMetadata(roomSession.metadata)
    const { external } = currentMetadata
    await updateMetadata({ ...external, title: newTitle })
  }

  const handleMeetingTitleChanged = tracked(
    _handleMeetingTitleChanged,
    { eventName: TrackEvent.ROOM_SESSION_RENAMED, eventDetails: getRoomTrackingDetails(roomSession) },
    {
      eventDetailsExtractor: (newTitle: string) => ({
        meetingTitle: newTitle,
      }),
    },
  )

  const isLive = isLiveSession(roomSession)
  const handleChangeLanguage = () => {
    setShowLanguageModal(true)
  }

  const handleLanguageClicked = tracked(handleChangeLanguage, {
    eventName: TrackEvent.CHANGE_MEETING_LANGUAGE_MODAL_CLICKED,
    eventDetails: { ...getRoomTrackingDetails(roomSession), source: 'manual' },
  })

  const handleUpdateLanguage = async (language: RoomSessionLanguageConfig) => {
    if (!roomSession) {
      return
    }
    const result = await updateRoomSessionConfig({
      accountId,
      roomId: roomSession.roomId,
      sessionId: roomSession.id,
      body: {
        language: language,
      },
    })
    if ('data' in result && result.data?.data) {
      dispatch(setRoomSession(result.data.data))
    }
  }

  return (
    <Container className='p-3 mw-100 w-100 gap-2 d-flex flex-column gap-4'>
      <Row className='m-0'>
        <Col className='px-0'>
          <Row className='m-0'>
            <Col className='p-0' xs='6'>
              {creatorServiceId ? (
                <h3 className='p-0 mb-2'>{meetingTitle}</h3>
              ) : (
                <EditableText
                  text={meetingTitle}
                  viewElement={({ children }) => <h3 className='p-0 mb-2'>{children}</h3>}
                  textOverflow={false}
                  hoverIcon
                  rows={1}
                  editIcon='bi-pencil'
                  onChange={handleMeetingTitleChanged}
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{ scope: 'room_session:update', superScope: ['room_session:admin'] }}
                />
              )}
            </Col>
          </Row>
          <Row className='d-flex m-0 align-items-center gap-2 gap-sm-3'>
            {creatorServiceId ? (
              <Col xs={12} sm='auto' className='p-0'>
                <CreatorType creator={creatorServiceId} />
              </Col>
            ) : null}
            <Col xs={12} sm='auto' className='p-0'>
              <MeetingType meetingType={source} />
            </Col>
            <Col xs={12} sm='auto' className='p-0'>
              <MeetingDateAndTime startedAt={startedAt} finishedAt={finishedAt} />
            </Col>
          </Row>
          <Row className='d-flex m-0 pt-3 align-items-center gap-2 gap-sm-3'>
            {isLive ? (
              <Col xs={12} sm='auto' className='p-0'>
                <LiveBadge startedAt={roomSession.startedAt} finishedAt={roomSession.finishedAt} />
              </Col>
            ) : null}
            <Col xs={12} sm='auto' className='p-0'>
              <AssistantStatusBadge assistantStatus={zocksAssistantStatus} />
            </Col>

            {hasMultiLanguageSupportQuota && multiLanguageEnabled && roomSessionLanguageConfig ? (
              <Col xs={12} sm='auto' className='p-0'>
                <>
                  <LanguageInfoButton languageConfig={roomSessionLanguageConfig} onLanguageClicked={handleLanguageClicked} />
                  {showLanguageModal ? (
                    <ChangeMeetingLanguageModal
                      onHide={() => setShowLanguageModal(false)}
                      participants={roomSession.users}
                      languageConfig={roomSessionLanguageConfig}
                      calculatedLanguageConfig={calculatedRoomSessionLanguageConfig}
                      onUpdateLanguage={handleUpdateLanguage}
                      isUpdatingSessionConfig={isUpdatingSessionConfig}
                      title={'Review Meeting Language'}
                      confirmButtonText={'Confirm'}
                    />
                  ) : null}
                </>
              </Col>
            ) : null}
            <Col xs={12} sm='auto' className='p-0'>
              {privateMeetingsEnabled || roomSession.isPrivate ? (
                <Col xs={12} sm='auto' className='p-0 meeting-summary-privacy'>
                  {privateMeetingsEnabled ? <PrivacyControl session={roomSession} /> : roomSession.isPrivate ? <PrivateBadge /> : null}
                </Col>
              ) : null}
            </Col>
          </Row>
        </Col>
        <Col xs='auto' className='px-0 meeting-info-change-meeting-options'>
          <Stack direction='horizontal' gap={3}>
            <ChangeMeetingOptions
              session={roomSession}
              onSessionChange={reloadRoomSession}
              skipEndMeeting={(!source || source === 'voice-memo') && !preparationStopOnly}
              buttonLabel='Change Meeting'
              hidePrivacyOption
            />
            <AddAIAssistantButton session={roomSession} variant='primary' />
          </Stack>
        </Col>
      </Row>
      {roomSession?.startedAt && expectedDurationMs && ['analyzing', 'inWaitingRoom'].includes(zocksAssistantStatus) ? (
        <Row>
          <MeetingProgressDisplay startedAtTime={new Date(roomSession.startedAt).getTime()} duration={expectedDurationMs} />
        </Row>
      ) : null}
    </Container>
  )
}
